import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  TrendingUp,
  BarChart2,
  Activity,
  MessageSquare,
  Bot,
  Building2,
  Users,
  Plus,
  RefreshCcw,
  LayoutGrid,
} from "lucide-react";
import type { PageKey } from "../../../lib/rbac";
// Safe to wire directly to onMouseEnter even though hovering can re-trigger
// it many times per session — a repeated import() of an already-fetched
// module resolves from the browser's module cache, not a new network
// request, so there's no need to debounce or guard against re-calls here.
import { routeImports } from "../../../routeImports";

// prefetch is typed () => void but routeImports entries actually return
// Promise<...> — that's intentional, not a mismatch. TypeScript allows a
// function returning any value where () => void is expected (the return
// value is just discarded), so routeImports.x can be assigned here directly
// without an extra `() => { routeImports.x(); }` wrapper. Nothing here
// awaits the import — onMouseEnter only needs to kick off the network
// fetch, not know when it resolves.
export type NavChild = {
  label: string;
  path: string;
  icon?: LucideIcon;
  /** Warms this route's lazy chunk on hover — see JAA-857. Omit for routes
   * with no dedicated lazy chunk (e.g. the Chatbot placeholder). */
  prefetch?: () => void;
};
export type NavItem = {
  label: string;
  path: string;
  icon?: LucideIcon;
  children?: NavChild[];
  /** Page key used to check role access — omit for items visible to all */
  pageKey?: PageKey;
  prefetch?: () => void;
};

export const STATIC_NAV_ITEMS: NavItem[] = [
  {
    label: "Admin Dashboard",
    path: "/admin/dashboard",
    icon: LayoutDashboard,
    pageKey: "admin_dashboard",
    prefetch: routeImports.adminDashboard,
  },
  {
    label: "Manage Admins",
    path: "/manage-admins",
    icon: Users,
    pageKey: "manage_admins",
    prefetch: routeImports.manageAdmins,
  },
  // {
  //   label: "Permissions Manager",
  //   path: "/permissions",
  //   icon: ShieldCheck,
  //   pageKey: "permissions_manager",
  // },
];

export const ADD_ORG_NAV_ITEM: NavItem = {
  label: "Add Organization",
  path: "/new-organization",
  icon: Plus,
  pageKey: "new_organization",
  prefetch: routeImports.addOrganization,
};

export const ORG_NAV_CHILDREN = (
  orgId: number,
  facilityId: number,
): NavChild[] => [
  {
    label: "Facility Overview",
    path: `/organizations/${orgId}/facilities/${facilityId}/overview`,
    icon: Building2,
    prefetch: routeImports.facilityOverview,
  },
  {
    label: "AI Insights",
    path: `/organizations/${orgId}/facilities/${facilityId}/ai-insights`,
    icon: Bot,
    prefetch: routeImports.aiInsights,
  },
  {
    label: "Scenario Simulator",
    path: `/organizations/${orgId}/facilities/${facilityId}/scenario-simulator`,
    icon: RefreshCcw,
    prefetch: routeImports.scenarioSimulator,
  },
  {
    label: "Performance Analysis",
    path: `/organizations/${orgId}/facilities/${facilityId}/performance-analysis`,
    icon: TrendingUp,
    prefetch: routeImports.performanceAnalysis,
  },
  {
    label: "Energy Analysis",
    path: `/organizations/${orgId}/facilities/${facilityId}/energy-analysis`,
    icon: BarChart2,
    prefetch: routeImports.energyAnalysis,
  },
  {
    label: "System Status",
    path: `/organizations/${orgId}/facilities/${facilityId}/system-status`,
    icon: Activity,
    prefetch: routeImports.systemStatus,
  },
  {
    label: "Custom Dashboards",
    path: `/organizations/${orgId}/facilities/${facilityId}/custom-dashboards`,
    icon: LayoutGrid,
    prefetch: routeImports.customDashboardsList,
  },
  {
    label: "Chatbot (JoulesAI)",
    path: `/organizations/${orgId}/facilities/${facilityId}/chatbot`,
    icon: MessageSquare,
    // No prefetch — renders the existing NotFound placeholder, not a
    // dedicated lazy chunk.
  },
];
